import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Monitor,
  ChefHat,
  RefreshCw,
  CheckCircle2,
  Clock,
  PlayCircle,
  CheckCheck,
  Utensils,
  Timer,
  ChevronDown,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { orderService } from '../services/order';
import { kitchenService } from '../services/kitchen';
import { tableService } from '../services/table';
import { webFcmService } from '../services/fcmService';
import { Order, OrderItem, Kitchen } from '../types';
import notificationSound from '../assets/sound.wav';
import { useRestaurantWebSocket } from '../hooks/useRestaurantWebSocket';

type KitchenCol = 'PENDING' | 'IN_PROGRESS' | 'READY';

const COL_CONFIG: Record<KitchenCol, { label: string; color: string; headerBg: string; cardBorder: string; icon: React.ReactNode }> = {
  PENDING:     { label: 'New Orders',  color: 'text-yellow-700', headerBg: 'bg-yellow-50 border-yellow-200', cardBorder: 'border-yellow-300', icon: <Clock className="h-5 w-5 text-yellow-500" /> },
  IN_PROGRESS: { label: 'In Progress', color: 'text-blue-700',   headerBg: 'bg-blue-50 border-blue-200',     cardBorder: 'border-blue-300',   icon: <PlayCircle className="h-5 w-5 text-blue-500" /> },
  READY:       { label: 'Ready',       color: 'text-green-700',  headerBg: 'bg-green-50 border-green-200',   cardBorder: 'border-green-300',  icon: <CheckCircle2 className="h-5 w-5 text-green-500" /> },
};

function elapsed(createdAt: string): string {
  const diff = Date.now() - new Date(createdAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m ago`;
}

// ── Kitchen Selector Modal ────────────────────────────────────────────────────

interface KitchenSelectorProps {
  kitchens: Kitchen[];
  onSelect: (kitchenId: string, kitchenName: string) => void;
  theme: any;
}

const KitchenSelector: React.FC<KitchenSelectorProps> = ({ kitchens, onSelect, theme }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
    <div className="rounded-2xl shadow-2xl p-8 w-full max-w-sm" style={{ background: theme.colors.background.card }}>
      <div className="flex flex-col items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: theme.colors.primary[600] }}>
          <ChefHat className="h-7 w-7 text-white" />
        </div>
        <div className="text-center">
          <h2 className="text-lg font-bold" style={{ color: theme.colors.text.primary }}>Select Kitchen</h2>
          <p className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>Choose which kitchen station you are at</p>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button
          onClick={() => onSelect('ALL', 'All Kitchens')}
          className="w-full py-3 px-4 rounded-xl font-medium text-sm text-left flex items-center gap-3 transition-all hover:opacity-80"
          style={{ background: theme.colors.background.primary, color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.primary}` }}
        >
          <Utensils className="h-4 w-4 opacity-60" />
          All Kitchens
        </button>
        {kitchens.map(k => (
          <button
            key={k.id}
            onClick={() => onSelect(k.id, k.name)}
            className="w-full py-3 px-4 rounded-xl font-semibold text-sm text-left flex items-center gap-3 transition-all hover:opacity-90 text-white"
            style={{ background: theme.colors.primary[600] }}
          >
            <ChefHat className="h-4 w-4" />
            {k.name}
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

export const KitchenDisplay: React.FC = () => {
  const { currentTheme } = useTheme();
  const { user } = useAuth();
  const restaurantId = getActiveRestaurantId();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [kitchensLoaded, setKitchensLoaded] = useState(false);
  const [tableMap, setTableMap] = useState<Record<string, string>>({});
  const [selectedKitchenId, setSelectedKitchenId] = useState<string | null>(null);
  const [selectedKitchenName, setSelectedKitchenName] = useState<string>('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<KitchenCol>('PENDING');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const soundPlayingRef = useRef(false);
  const prevOrderIdsRef = useRef<Set<string> | null>(null);

  // Pre-load audio with loop enabled
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.8;
    audioRef.current.loop = true;
  }, []);

  // Load kitchen list + tables
  useEffect(() => {
    if (!user?.organizationCode || !restaurantId) return;
    kitchenService.getKitchens(user.organizationCode, restaurantId)
      .then(data => { setKitchens(data); setKitchensLoaded(true); })
      .catch(() => setKitchensLoaded(true));
    tableService.getTables({ org: user.organizationCode, restaurantId })
      .then(data => {
        const map: Record<string, string> = {};
        data.forEach(t => { map[t.id] = t.name; });
        setTableMap(map);
      })
      .catch(() => {});
  }, [user?.organizationCode, restaurantId]);

  const startSound = useCallback(() => {
    if (audioRef.current && !soundPlayingRef.current) {
      soundPlayingRef.current = true;
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const stopSound = useCallback(() => {
    if (audioRef.current && soundPlayingRef.current) {
      soundPlayingRef.current = false;
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const fetchOrders = useCallback(async (silent = false, checkForNew = false) => {
    if (!restaurantId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const data = await orderService.getKitchenOrders(restaurantId);
      // Play sound only when a genuinely new order ID appears (not on accept / item-ready / settle)
      if (checkForNew && prevOrderIdsRef.current !== null) {
        const hasNew = data.some(o => !prevOrderIdsRef.current!.has(o.id));
        if (hasNew) startSound();
      }
      prevOrderIdsRef.current = new Set(data.map(o => o.id));
      setOrders(data);
    } catch {
      if (!silent) toast.error('Failed to load kitchen orders');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId, startSound]);

  // Initial fetch + 60s fallback poll after kitchen is selected
  useEffect(() => {
    if (!selectedKitchenId) return;
    fetchOrders();
    intervalRef.current = setInterval(() => fetchOrders(true), 60000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [selectedKitchenId, fetchOrders]);

  // WebSocket — live kitchen updates; checkForNew=true so sound only plays for genuinely new orders
  useRestaurantWebSocket({
    onKitchenUpdate: useCallback(() => {
      if (selectedKitchenId) fetchOrders(true, true);
    }, [selectedKitchenId, fetchOrders]),
  });

  // Listen for FCM ORDER_PLACED → sound + refresh
  useEffect(() => {
    if (!selectedKitchenId) return;
    let unsubscribe: (() => void) | null = null;
    webFcmService.onForegroundMessage((title, body, data) => {
      if (data.type === 'ORDER_PLACED' || data.type === 'NEW_ORDER') {
        startSound();
        fetchOrders(true);
        toast(`🍽️ ${title}: ${body}`, { duration: 5000 });
      }
    }).then(fn => { unsubscribe = fn; });
    return () => { if (unsubscribe) unsubscribe(); };
  }, [selectedKitchenId, fetchOrders, startSound]);

  const handleKitchenSelect = (kitchenId: string, kitchenName: string) => {
    setSelectedKitchenId(kitchenId);
    setSelectedKitchenName(kitchenName);
  };

  const handleAccept = async (orderId: string) => {
    setActionLoading(`accept-${orderId}`);
    try {
      const updated = await orderService.kitchenAcceptOrder(orderId);
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o));
      toast.success('Order accepted');
    } catch {
      toast.error('Failed to accept order');
    } finally {
      setActionLoading(null);
    }
  };

  const handleItemReady = async (orderId: string, itemIndex: number) => {
    setActionLoading(`item-${orderId}-${itemIndex}`);
    try {
      const updated = await orderService.kitchenMarkItemReady(orderId, itemIndex);
      setOrders(prev => prev.map(o => o.id === orderId ? updated : o));
      if (updated.kitchenStatus === 'READY') {
        toast.success(`Order #${updated.orderNo} — all items ready! Waiter notified.`);
      }
    } catch {
      toast.error('Failed to mark item ready');
    } finally {
      setActionLoading(null);
    }
  };

  // Filter items visible to the selected kitchen
  const getVisibleItems = (order: Order): OrderItem[] =>
    selectedKitchenId === 'ALL'
      ? order.items
      : order.items.filter(i => i.kitchenId === selectedKitchenId);

  // Filter orders that have at least 1 visible item
  const filteredOrders = orders.filter(o => getVisibleItems(o).length > 0);

  // Determine column based on visible items' readiness
  const getColForOrder = (order: Order): KitchenCol => {
    const visible = getVisibleItems(order);
    if (visible.length === 0) return 'PENDING';
    if (visible.every(i => i.kitchenStatus === 'READY')) return 'READY';
    if (visible.some(i => i.kitchenStatus === 'READY') || order.kitchenStatus === 'IN_PROGRESS') return 'IN_PROGRESS';
    return 'PENDING';
  };

  const columns: Record<KitchenCol, Order[]> = {
    PENDING:     filteredOrders.filter(o => getColForOrder(o) === 'PENDING'),
    IN_PROGRESS: filteredOrders.filter(o => getColForOrder(o) === 'IN_PROGRESS'),
    READY:       filteredOrders.filter(o => getColForOrder(o) === 'READY'),
  };

  // Stop looping sound when no PENDING orders remain
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (columns.PENDING.length === 0) stopSound(); }, [columns.PENDING.length]);

  // ── No restaurant selected ─────────────────────────────────────────────────
  if (!restaurantId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-500">
        <ChefHat className="h-12 w-12 opacity-30" />
        <p className="text-sm">No restaurant selected. Select a restaurant from the top bar.</p>
      </div>
    );
  }

  // ── Kitchen selector modal (shown until kitchen is chosen) ─────────────────
  if (kitchensLoaded && !selectedKitchenId) {
    return <KitchenSelector kitchens={kitchens} onSelect={handleKitchenSelect} theme={currentTheme} />;
  }

  if (!kitchensLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // ── Main KDS view ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen p-4 md:p-6" style={{ background: currentTheme.colors.background.primary }}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: currentTheme.colors.primary[600] }}>
            <Monitor className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>Kitchen Display</h1>
            <p className="text-xs text-gray-400">{filteredOrders.length} order{filteredOrders.length !== 1 ? 's' : ''} · live updates</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Kitchen switcher */}
          <button
            onClick={() => setSelectedKitchenId(null)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all"
            style={{ background: currentTheme.colors.primary[600], color: '#fff' }}
          >
            <ChefHat className="h-4 w-4" />
            {selectedKitchenName}
            <ChevronDown className="h-3 w-3 opacity-70" />
          </button>

          <button
            onClick={() => fetchOrders(true)}
            disabled={refreshing || loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
            style={{ background: currentTheme.colors.background.card, color: currentTheme.colors.text.primary, border: `1px solid ${currentTheme.colors.border.primary}` }}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl mb-4" style={{ background: currentTheme.colors.background.card, border: `1px solid ${currentTheme.colors.border.primary}` }}>
        {(['PENDING', 'IN_PROGRESS', 'READY'] as KitchenCol[]).map(col => {
          const cfg = COL_CONFIG[col];
          const count = loading ? 0 : columns[col].length;
          const isActive = activeTab === col;
          return (
            <button
              key={col}
              onClick={() => setActiveTab(col)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${isActive ? cfg.headerBg + ' border ' + cfg.cardBorder : ''}`}
            >
              {cfg.icon}
              <span className={isActive ? cfg.color : 'text-gray-500'}>{cfg.label}</span>
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${isActive ? cfg.color + ' ' + cfg.headerBg + ' border ' + cfg.cardBorder : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-40 bg-gray-200 rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 items-start">
          {columns[activeTab].length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
              <Utensils className="h-10 w-10 opacity-20" />
              <p className="text-sm">No {COL_CONFIG[activeTab].label.toLowerCase()}</p>
            </div>
          ) : (
            columns[activeTab].map(order => (
              <OrderCard
                key={order.id}
                order={order}
                col={activeTab}
                visibleItems={getVisibleItems(order)}
                actionLoading={actionLoading}
                borderColor={currentTheme.colors.primary[400]}
                tableName={tableMap[order.tableId] || `Table ${order.tableId?.slice(-4) || '—'}`}
                onAccept={handleAccept}
                onItemReady={handleItemReady}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ── Order Card ────────────────────────────────────────────────────────────────

interface OrderCardProps {
  order: Order;
  col: KitchenCol;
  visibleItems: OrderItem[];
  actionLoading: string | null;
  borderColor: string;
  tableName: string;
  onAccept: (orderId: string) => void;
  onItemReady: (orderId: string, itemIndex: number) => void;
}

const OrderCard: React.FC<OrderCardProps> = ({ order, col, visibleItems, actionLoading, borderColor, tableName, onAccept, onItemReady }) => {
  const readyCount = visibleItems.filter(i => i.kitchenStatus === 'READY').length;
  const totalCount = visibleItems.length;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" style={{ border: `2px solid ${borderColor}` }}>
      {/* Header */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <span className="font-bold text-gray-800 text-base">{tableName}</span>
          <div className="flex items-center gap-2">
            {col === 'IN_PROGRESS' && (
              <span className="text-xs text-gray-400">{readyCount}/{totalCount} ready</span>
            )}
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Timer className="h-3 w-3" />
              {elapsed(order.createdAt)}
            </span>
          </div>
        </div>
        <span className="text-xs text-gray-400 font-medium">#{order.orderNo || order.id.slice(-6)}</span>
      </div>

      <div className="px-4 py-3 space-y-2">
        {visibleItems.map((item, visIdx) => {
          const realIndex = order.items.indexOf(item);
          return (
            <ItemRow
              key={visIdx}
              item={item}
              index={realIndex}
              order={order}
              col={col}
              actionLoading={actionLoading}
              onItemReady={onItemReady}
            />
          );
        })}
        {order.notes && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-1.5">
            📝 {order.notes}
          </p>
        )}
      </div>

      {col === 'PENDING' && (
        <div className="px-4 pb-3">
          <button
            onClick={() => onAccept(order.id)}
            disabled={actionLoading === `accept-${order.id}`}
            className="w-full py-2 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60 transition-all"
            style={{ background: '#3b82f6' }}
          >
            {actionLoading === `accept-${order.id}` ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <PlayCircle className="h-4 w-4" />
            )}
            Accept Order
          </button>
        </div>
      )}

      {col === 'READY' && (
        <div className="px-4 pb-3 flex items-center gap-2 text-green-600">
          <CheckCheck className="h-4 w-4" />
          <span className="text-xs font-semibold">All items ready — waiter notified</span>
        </div>
      )}
    </div>
  );
};

// ── Item Row ──────────────────────────────────────────────────────────────────

interface ItemRowProps {
  item: OrderItem;
  index: number;
  order: Order;
  col: KitchenCol;
  actionLoading: string | null;
  onItemReady: (orderId: string, itemIndex: number) => void;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, index, order, col, actionLoading, onItemReady }) => {
  const isReady = item.kitchenStatus === 'READY';
  const loadingKey = `item-${order.id}-${index}`;

  return (
    <div className={`flex items-center gap-3 py-1.5 px-2 rounded-lg transition-all ${isReady ? 'bg-green-50' : 'bg-transparent'}`}>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isReady ? 'text-green-700 line-through opacity-60' : 'text-gray-800'}`}>
          <span className="font-extrabold mr-1" style={{ color: isReady ? '#16a34a' : '#4f46e5' }}>{item.quantity}×</span>
          {item.name}
        </p>
        {item.specialNotes && (
          <p className="text-xs text-amber-600 truncate">{item.specialNotes}</p>
        )}
      </div>

      {col === 'IN_PROGRESS' && !isReady && (
        <button
          onClick={() => onItemReady(order.id, index)}
          disabled={actionLoading === loadingKey}
          className="flex-shrink-0 px-2 py-1 rounded-lg flex items-center gap-1 text-white text-xs font-semibold disabled:opacity-60 transition-all"
          style={{ background: '#22c55e' }}
        >
          {actionLoading === loadingKey ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5" />
              Ready
            </>
          )}
        </button>
      )}

      {isReady && <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />}
    </div>
  );
};
